import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useNotes } from '../context/NoteContext';
import { useToast } from '../hooks/use-toast';
import { Excalidraw } from '@excalidraw/excalidraw';
import styled from 'styled-components';
import excalidrawService from '../services/excalidrawService';
import fileService from '../services/fileService';
import { FaPencilAlt, FaPlus, FaEdit, FaUsers } from 'react-icons/fa';
import SketchEditor from './SketchEditor';
import { useAuth } from '../context/AuthContext';

// Error boundary to catch Excalidraw errors
export class ExcalidrawErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Excalidraw error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          padding: '20px', 
          border: '1px solid #f5c6cb', 
          borderRadius: '4px',
          backgroundColor: '#f8d7da', 
          color: '#721c24'
        }}>
          <h3>Something went wrong with the drawing component</h3>
          <p>Please try refreshing the page.</p>
          <details>
            <summary>Error details</summary>
            <pre>{this.state.error?.toString()}</pre>
          </details>
        </div>
      );
    }

    return this.props.children;
  }
}

const ExcalidrawComponent = ({ initialData }) => {
  const { user, isAuthenticated } = useAuth();
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [excalidrawData, setExcalidrawData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSketchEditorOpen, setIsSketchEditorOpen] = useState(false);
  const [isPlaceholder, setIsPlaceholder] = useState(false);
  const [sketchId, setSketchId] = useState(null);
  
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { noteId } = useParams();
  const { getNoteById, updateNote } = useNotes();
  const { toast } = useToast();

  // Add a ref to track the current placeholder position
  const placeholderPositionRef = useRef(null);

  useEffect(() => {
    // Check if initialData is a reference to a sketch
    const fetchSketchIfReference = async () => {
      //console.log("Initial data:", initialData); // Debug log
      
      // Extract reference ID from initialData in various formats
      let refId = null;
      
      if (typeof initialData === 'string') {
        // Direct string reference
        refId = initialData.trim();
      } else if (initialData && initialData.ref) {
        // Object with ref property
        refId = initialData.ref;
      } else if (initialData && typeof initialData === 'object') {
        // Try to find ref in any format
        const objString = JSON.stringify(initialData);
        const refMatch = objString.match(/"ref":"([^"]+)"/);
        if (refMatch && refMatch[1]) {
          refId = refMatch[1];
        }
      }
      
      if (!refId) {
        //console.log("No valid reference ID found in:", initialData);
        setIsPlaceholder(true);
        return;
      }
      
      //console.log("Found refId:", refId); // Debug log
        
      // Check if it's a placeholder
      if (refId.startsWith('create-new-sketch-id') || refId === 'placeholder') {
        //console.log("Found placeholder with ID:", refId); // Debug log
        setIsPlaceholder(true);
        placeholderPositionRef.current = refId; // Store the exact ID for later
        return;
      }
      
      setLoading(true);
      setError(null);
      setSketchId(refId);
      
      try {
        // Fetch the sketch data
        const sketch = await excalidrawService.getSketch(refId);
        
        if (sketch && sketch.content) {
          //console.log("Loaded sketch data:", sketch);
          
          // Process files to convert URLs to blob URLs for Excalidraw
          const processedContent = { ...sketch.content };
          if (sketch.content.files) {
            const processedFiles = {};
            for (const [fileId, fileData] of Object.entries(sketch.content.files)) {
              try {
                if (fileData.dataURL && (fileData.dataURL.startsWith('http') || fileData.dataURL.startsWith('/assets/'))) {
                  // This is a stored URL, convert it to a blob URL
                  const response = await fetch(fileData.dataURL);
                  const blob = await response.blob();
                  const blobUrl = URL.createObjectURL(blob);
                  
                  processedFiles[fileId] = {
                    ...fileData,
                    dataURL: blobUrl,
                    originalUrl: fileData.dataURL // Store the original URL
                  };
                } else {
                  // This is already a blob URL or data URL, keep as is
                  processedFiles[fileId] = fileData;
                }
              } catch (error) {
                console.error(`Error loading image ${fileId}:`, error);
                // Skip this file if it can't be loaded
              }
            }
            processedContent.files = processedFiles;
          }
          
          setExcalidrawData(processedContent);
        } else if (sketch && typeof sketch === 'object') {
          // Some sketches might have the data directly
          setExcalidrawData(sketch);
        } else {
          throw new Error("Sketch data not found or in invalid format");
        }
      } catch (err) {
        console.error("Error fetching sketch:", err);
        setError(err.message || "Failed to load sketch");
        toast({
          title: "Error",
          description: "Failed to load sketch data",
          variant: "destructive"
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSketchIfReference();
  }, [initialData, toast]);

  const handleCreateNewSketch = () => {
    //console.log("Creating new sketch with placeholder ID:", placeholderPositionRef.current); // Debug log
    // Open the sketch editor modal
    setIsSketchEditorOpen(true);
  };

  const handleCloseSketchEditor = async (newSketchId) => {
    setIsSketchEditorOpen(false);
    
    if (newSketchId && noteId) {
      try {
        //console.log("Saving sketch with ID:", newSketchId);
        //console.log("Current placeholder ID:", placeholderPositionRef.current);
        
        // Update the note with the new sketch ID
        const note = await getNoteById(noteId);
        if (note) {
          const replacement = `\`\`\`excalidraw\nref="${newSketchId}"\n\`\`\``;
          
          // Use a very direct approach to find and replace the placeholder
          // The key insight is that we're just looking for any variant of create-new-sketch-id
          let updatedContent;
          
          if (placeholderPositionRef.current) {
            // If we have a specific placeholder ID, look for that exact ID
            // Make sure to escape any special characters in the ID
            const escapedId = placeholderPositionRef.current.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
            
            const pattern = new RegExp(
              '```excalidraw\\s*' +
              'ref="' + escapedId + '"\\s*' +
              '```', 
              'g'
            );
            
            //console.log("Searching for placeholder pattern:", pattern);
            
            if (note.content.match(pattern)) {
              //console.log("Found exact placeholder match");
              updatedContent = note.content.replace(pattern, replacement);
            } else {
              // Fallback to searching for any create-new-sketch-id placeholder
              //console.log("No exact match found, trying general placeholder");
              const generalPattern = /```excalidraw\s*ref="create-new-sketch-id[^"]*"\s*```/;
              updatedContent = note.content.replace(generalPattern, replacement);
            }
          } else {
            // No specific ID, just replace the first placeholder we find
            //console.log("No placeholder ID stored, using general pattern");
            const generalPattern = /```excalidraw\s*ref="create-new-sketch-id[^"]*"\s*```/;
            updatedContent = note.content.replace(generalPattern, replacement);
          }
          
          //console.log("Updated content:", updatedContent);
          
          // Update the note with new content
          await updateNote(note.id, {
            ...note,
            content: updatedContent
          });
          
          toast({
            title: "Success",
            description: "Sketch created and linked to note!"
          });
          
          // Use window.location.reload() as a more reliable way to refresh the page
          window.location.reload();
        }
      } catch (error) {
        console.error("Error updating note with sketch reference:", error);
        toast({
          title: "Error",
          description: "Failed to update note with sketch reference",
          variant: "destructive"
        });
      }
    }
  };

  const handleEditSketch = () => {
    if (sketchId) {
      // Navigate to the sketch editor screen with the current sketch ID
      navigate(`/s/${sketchId}`);
    } else {
      toast({
        title: "Error",
        description: "Cannot edit sketch: No sketch ID found",
        variant: "destructive"
      });
    }
  };

  const handleCollaborationClick = () => {
    if (sketchId) {
      const collaborationUrl = `https://draw.orgwi.se?documentId=${sketchId}&collaborationMode=true&autoSave=true`;
      window.location.href = collaborationUrl;
    } else {
      toast({
        title: "Error",
        description: "Cannot start collaboration: No sketch ID found",
        variant: "destructive"
      });
    }
  };

  // Create a safe initial data object with only the required properties
  const safeInitialData = React.useMemo(() => {
    if (!excalidrawData) return { elements: [], appState: { viewBackgroundColor: '#FFFFFF' }, files: {} };
    
    // Only include the essential properties
    return {
      elements: Array.isArray(excalidrawData.elements) ? excalidrawData.elements : [],
      appState: {
        viewBackgroundColor: '#FFFFFF',
        currentItemFontFamily: 1,
        ...(excalidrawData.appState && typeof excalidrawData.appState === 'object' 
          ? Object.fromEntries(
              Object.entries(excalidrawData.appState).filter(([k]) => 
                !['collaborators', 'collaboratorCount'].includes(k)
              )
            ) 
          : {})
      },
      files: excalidrawData.files || {}
    };
  }, [excalidrawData]);

  if (loading) {
    return <div>Loading sketch...</div>;
  }

  if (error) {
    return (
      <PlaceholderContainer>
        <div style={{ color: '#dc3545', marginBottom: '1rem' }}>
          <p>Error loading sketch: {error}</p>
          <p>The sketch ID might be invalid or the sketch might have been deleted.</p>
        </div>
        <CreateButton onClick={handleCreateNewSketch}>
          <FaPlus style={{ marginRight: '8px' }} />
          Create New Sketch Instead
        </CreateButton>
        
        <SketchEditor
          isOpen={isSketchEditorOpen}
          onClose={handleCloseSketchEditor}
          noteId={noteId}
          initialSketchId={null}
        />
      </PlaceholderContainer>
    );
  }

  if (isPlaceholder) {
    return (
      <PlaceholderContainer ref={containerRef}>
        <p>Create a new sketch to add here</p>
        <CreateButton onClick={handleCreateNewSketch}>
          <FaPlus style={{ marginRight: '8px' }} />
          Create Sketch
        </CreateButton>
        
        <SketchEditor
          isOpen={isSketchEditorOpen}
          onClose={handleCloseSketchEditor}
          noteId={noteId}
          initialSketchId={null}
        />
      </PlaceholderContainer>
    );
  }

  // For existing sketches, display them and add edit button
  return (
    <ExcalidrawContainer>
      <DrawingArea>
        <ExcalidrawErrorBoundary>
          <Excalidraw
            initialData={safeInitialData}
            viewModeEnabled={true}
            theme="light"
            UIOptions={{
              canvasActions: {
                loadScene: false,
                saveAsScene: false,
                saveScene: false,
                export: false,
              }
            }}
          />
        </ExcalidrawErrorBoundary>
      </DrawingArea>
      
      <Controls>
        <EditButton onClick={handleEditSketch}>
          <FaEdit style={{ marginRight: '8px' }} />
          Edit Sketch
        </EditButton>
        <EditButton onClick={handleCollaborationClick} style={{ marginLeft: '8px' }}>
          <FaUsers style={{ marginRight: '8px' }} />
          Collaboration Mode
        </EditButton>
      </Controls>
    </ExcalidrawContainer>
  );
};

// Styled components
const PlaceholderContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px dashed #ced4da;
  min-height: 200px;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  margin-top: 1rem;
  
  &:hover {
    background-color: #0069d9;
  }
`;

const ExcalidrawContainer = styled.div`
  position: relative;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  overflow: hidden;
  background: white;
`;

const DrawingArea = styled.div`
  height: 400px;
  width: 100%;
`;

const Controls = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
`;

const EditButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.5rem 0.75rem;
  background-color: white;
  color: #495057;
  border: 1px solid #ced4da;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &:hover {
    background-color: #f8f9fa;
  }
`;

export default ExcalidrawComponent; 